import React from 'react';

import ProgressBar from '@ramonak/react-progress-bar';
import { Accept, ErrorCode, useDropzone } from 'react-dropzone';

import { IntegrationBar } from './integration-bar';
import { useFileUploadLogic } from './useFileUploadLogic';
import { getErrorMessage } from './utils';
import { Dialog, DialogContent } from '../shadcn/dialog';
import { ActiveJob } from '@/hooks/useJobProgress';
import { RelativityIntegrationModal } from '@/new/components/dialogs/relativity/relativity-integration-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';

interface FileUploaderProps {
  isUploading: boolean;
  onDrop: (acceptedFiles: File[]) => void;
  successfulUploads?: number;
  totalProgress?: number;
  acceptedTypes?: Accept;
  maxFiles?: number;
  maxSize?: number;
  matterId?: string;
  caseId?: string | null;
  onApideckSuccess?: () => void;
  isApideckEnabled?: boolean;
  activeJobs?: ActiveJob[];
}

export function FileUploadInput({
  isUploading,
  onDrop,
  acceptedTypes,
  maxFiles,
  maxSize,
  totalProgress,
  matterId,
  caseId,
  onApideckSuccess,
  successfulUploads = 1,
  isApideckEnabled,
  activeJobs,
}: FileUploaderProps) {
  const {
    files,
    localError,
    handleFileAccepted,
    modalOpen,
    setModalOpen,
    modalContent,
    isRelativityModalOpen,
    setIsRelativityModalOpen,
    apiDeckSessionToken,
    apiDeckConnections,
    connectRelativity,
    handleOpenApideckModal,
    handleSelectRelativityList,
  } = useFileUploadLogic({
    caseId,
    isApideckEnabled,
    maxFiles,
    onApideckSuccess,
    activeJobs,
  });

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    acceptedFiles,
    fileRejections = [],
  } = useDropzone({
    multiple: maxFiles !== 1,
    noClick: maxFiles === files.length,
    onDropAccepted: (acceptedFiles) => handleFileAccepted(acceptedFiles, onDrop),
    accept: acceptedTypes,
    maxSize,
    maxFiles,
  });

  const handleApideckModal = (connection: string, modalContent: React.ReactNode) => {
    handleOpenApideckModal(connection, modalContent);
  };

  return (
    <>
      {matterId && (
        <IntegrationBar
          matterId={matterId}
          isApideckEnabled={isApideckEnabled}
          apiDeckConnections={apiDeckConnections}
          apiDeckSessionToken={apiDeckSessionToken}
          onRelativityConnect={connectRelativity}
          onApideckModal={handleApideckModal}
          onApideckSuccess={onApideckSuccess}
        />
      )}

      <div
        data-testid="file-uploader"
        className={`relative mt-2 flex max-h-56 min-h-40 items-center justify-center rounded-lg border-2 border-dashed bg-slate-50 ${
          maxFiles === files.length ? 'cursor-default' : 'cursor-pointer'
        }`}
        {...getRootProps()}
      >
        <input {...getInputProps()} data-testid="upload-files-input" />

        {isDragActive ? (
          <p className="text-gray-600">Drop the files here ...</p>
        ) : isUploading ? (
          <div className="flex flex-col">
            <div className="mb-4 text-center text-base leading-6 font-bold text-gray-900 not-italic">
              Uploaded {successfulUploads} of {acceptedFiles.length} files
            </div>
            <ProgressBar
              completed={totalProgress ?? 0}
              customLabel={''}
              bgColor={'#4161FF'}
              height={'6px'}
              className="animate-pulse"
              isLabelVisible={false}
            />
          </div>
        ) : (
          <div>
            <Button variant="primary" onClick={() => null}>
              Upload Files
              <Icon className="lucide--folder-plus" />
            </Button>
            <p className="mt-2 text-center text-sm leading-4 text-gray-600">Or drag here</p>
          </div>
        )}

        <div className="absolute bottom-2 left-3">
          <p
            className={`h-4 text-xs text-red-400 transition-opacity ${
              fileRejections[0] || localError ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {getErrorMessage({
              errorCode: localError || (fileRejections[0]?.errors[0].code as ErrorCode),
              acceptedFileTypes: acceptedTypes,
              maxFiles,
              maxSize,
            })}
          </p>
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>{modalContent}</DialogContent>
      </Dialog>

      <Dialog open={isRelativityModalOpen} onOpenChange={setIsRelativityModalOpen}>
        <DialogContent>
          <RelativityIntegrationModal
            caseId={caseId ?? ''}
            onSelectList={handleSelectRelativityList}
            onClose={() => setIsRelativityModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
